import type { FormEntryVariable } from '../../API';

export const getInitials = (name: string | undefined): string =>
  (name ?? '')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

// Capitalize first letter of a string
export const capitalizeFirstLetter = (string: string | undefined) => {
  if (!string) return;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const capitalizeFirstLetterEachWord = (string: string | undefined) => {
  if (!string) return;
  return string.replace(
    /\w\S*/g,
    txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
};

export const capitalizeFirstLetterFirstWord = (string: string | undefined) => {
  if (!string) return;
  return string.replace(
    /^\w\S*/,
    txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
};

export const decodeString = (str: string) => {
  const txt = new DOMParser().parseFromString(str, 'text/html');
  return txt.documentElement.textContent ?? str;
};

export const buildLinksAndParse = (text: string, decode?: boolean) => {
  const decodedText = decode ? decodeString(text) : text;

  // Remove invalid HTML tags that look like email addresses (e.g., <email@domain.com>)
  // This prevents them from being parsed as HTML elements
  // Handles both self-closing and paired tags: <email@domain.com> or <email@domain.com>content</email@domain.com>
  const invalidEmailTagRegex =
    /<([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?:>([\s\S]*?)<\/\1>|\s*\/?>)/g;
  const cleanedText = decodedText.replace(
    invalidEmailTagRegex,
    (_match, email, content) => {
      // If there's content between opening and closing tags, preserve it, otherwise just the email
      return content !== undefined ? content : email;
    }
  );

  const urlRegex =
    /(<img\s[^>]*>|<a(?:\s[^>]*)?>[\s\S]*?<\/a>)|(?:(?:https?|file):\/\/|www\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gi;
  const parsedText = cleanedText.replace(urlRegex, (url, tag) => {
    // anchor already exists
    if (tag) return url;

    return `<a href="${url}" target="_blank" rel="noreferrer">${url}</a>`;
  });

  return decode ? parsedText.replace(/\n/g, '<br>') : parsedText;
};

export const centerTruncate = (text: string, maxLength: number) => {
  // return early if text is not max
  if (text.length <= maxLength) return text;

  const charSplit = (maxLength - 3) / 2;
  // if the result is an odd number, add the extra char in the front half
  const start = text.substring(0, Math.round(charSplit));
  const end = text.substring(text.length - Math.floor(charSplit), text.length);
  return `${start}...${end}`;
};

export const getFileExtension = (name: string) => name.split('.').pop();

export const centerTruncateExtension = (text: string, maxLength: number) => {
  // return early if text is not max
  if (text.length <= maxLength) return text;

  // get file extension
  const extension = getFileExtension(text);

  // subtract length of file extension + char length of ellipsis
  const charSplit = maxLength - (3 + (extension as string).length);

  const start = text.substring(0, charSplit);
  return `${start}...${extension}`;
};

export const hasWhiteSpace = (text: string) => {
  return /\s/.test(text);
};

export function isValidLuhn(value: string) {
  // The Luhn Algorithm to test card number validity
  let nCheck = 0;
  let isEven = false;

  for (let n = value.length - 1; n >= 0; n--) {
    let nDigit = Number.parseInt(value[n], 10);

    if (isEven) {
      if ((nDigit *= 2) > 9) nDigit -= 9;
    }

    nCheck += nDigit;
    isEven = !isEven;
  }

  return nCheck % 10 === 0;
}

export const redactGiftCardNumber = (longString: string) => {
  const regex = /(gift_card_number:\s*)(\d{16})/g;

  return longString.replace(regex, (match, prefix, number) => {
    return isValidLuhn(number) ? `${prefix}################` : match;
  });
};

export const redactCreditCards = (
  text: string,
  sensitive?: FormEntryVariable['sensitive']
) => {
  if (sensitive) return text;
  //16-digit number or 15-digit number
  //number in the format XXXX XXXX XXXX XXXX (4 digits, space, 4 digits, space, 4 digits)
  //number in the format XXXX-XXXX-XXXX-XXXX (4 digits, hyphen, 4 digits, hyphen, 4 digits)
  //number in the format XXXX XXXXXX XXXXX (4 digits, space, 6 digits, space, 5 digits)
  //number in the format XXXX-XXXXXX-XXXXX (4 digits, hyphen, 6 digits, hyphen, 5 digits)
  const pattern =
    /[0-9]{16}|[0-9]{4} [0-9]{4} [0-9]{4} [0-9]{4}|[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{4}|[0-9]{15}|[0-9]{4} [0-9]{6} [0-9]{5}|[0-9]{4}-[0-9]{6}-[0-9]{5}/g;
  const matches = text.match(pattern);

  if (!matches) {
    return text;
  }

  matches.forEach(match => {
    const cleanMatch = match.replace(/\D/g, '');
    if (isValidLuhn(cleanMatch)) {
      text = text.replace(match, '#### #### #### ####');
    }
  });
  return text;
};

export const formatPhoneNumber = (phone: string) => {
  return phone
    .replace(/\D/g, '')
    .replace(
      /(\d*)(\d{3})(\d{3})(\d{4})$/,
      (_s, a, b, c, d) => `+${a} (${b}) ${c}-${d}`
    )
    .replace(/\+(1\b|\s)\s*/, '');
};

export const validateEmail = (email?: string) => {
  if (!email) return false;
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

export const highlightWordInString = (
  text: string,
  wordToHighlight: string | undefined
): string => {
  if (!wordToHighlight) return text;
  // Escape special regex characters in case wordToHighlight contains them
  const escapedWord = wordToHighlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedWord})`, 'gi');
  return text.replace(regex, '<strong>$1</strong>');
};

export const applyMask = (
  value: string,
  mask: string | (string | RegExp)[]
): string => {
  if (typeof mask === 'string') {
    let maskedValue = '';
    let valueIndex = 0;

    for (let i = 0; i < mask.length && valueIndex < value.length; i++) {
      if (mask[i] === '9') {
        if (/\d/.test(value[valueIndex])) {
          maskedValue += value[valueIndex];
          valueIndex++;
        } else {
          break;
        }
      } else {
        maskedValue += mask[i];
      }
    }

    return maskedValue;
  }

  return value;
};

export const applyPhoneMask = (value: string, mask: string): string => {
  const numbers = value.replace(/\D/g, '');
  let formatted = '';
  let numberIndex = 0;

  for (let i = 0; i < mask.length && numberIndex < numbers.length; i++) {
    if (mask[i] === '9') {
      formatted += numbers[numberIndex];
      numberIndex++;
    } else {
      formatted += mask[i];
    }
  }

  return formatted;
};

export const formatCurrency = (
  amount: string | number | null | undefined
): string => {
  if (!amount) return 'Not added';
  return `$${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatStatus = (
  status: 'COMPLETED' | 'IN_PROGRESS' | 'SCHEDULED'
) => {
  const statusMap = {
    COMPLETED: 'Completed',
    IN_PROGRESS: 'In progress',
    SCHEDULED: 'Scheduled',
  };
  return statusMap[status];
};

export function getSerialSubstrings(value: string): {
  prefix: string;
  suffix: string;
} {
  const substrings = value.split(' ');
  return {
    prefix: substrings.slice(0, 2).join(' '),
    suffix: substrings.slice(2, 4).join(' '),
  };
}

export function isValidSerialLength(value: string): boolean {
  return [12, 16].includes(value.replace(/\s/g, '').length);
}

export function getCalculatedCardCount(
  startSerialValue: string,
  endSerialValue: string
): number {
  if (
    !isValidSerialLength(startSerialValue) ||
    !isValidSerialLength(endSerialValue)
  ) {
    return 0;
  }

  const { prefix: startPrefix, suffix: startSuffix } =
    getSerialSubstrings(startSerialValue);
  const { prefix: endPrefix, suffix: endSuffix } =
    getSerialSubstrings(endSerialValue);

  const cleanStartSuffix = startSuffix.replace(/\s/g, '').slice(0, 4);
  const cleanEndSuffix = endSuffix.replace(/\s/g, '').slice(0, 4);

  if (
    startPrefix !== endPrefix ||
    !/^\d+$/.test(cleanStartSuffix) ||
    !/^\d+$/.test(cleanEndSuffix)
  ) {
    return 0;
  }

  const calculatedCount = Number(cleanEndSuffix) - Number(cleanStartSuffix) + 1;
  return calculatedCount >= 1 ? calculatedCount : 0;
}
